// Admin card (Settings → Integrations): per-tenant employee-visibility toggles for the
// financial-products surfaces. Mirrors BenefitsSettingsCard: the employer decides
// whether employees see My Products (marketplace) and Loans & Advances at all — the
// second key of the two-key gate (the platform entitlement is the first). Employee-
// facing only: admin pages (Loans & Advances config, Marketplace) are never gated here.
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import { Coins, Loader2 } from 'lucide-react';
import { creditApi, type CreditModuleSettings } from '@/features/credit/api/creditApi';
import { resetMarketplaceVisible } from '@/features/marketplace/useMarketplaceVisible';

type Key = keyof CreditModuleSettings;

const ROWS: Array<{ key: Key; label: string; desc: string }> = [
  {
    key: 'showEmployeeMarketplace',
    label: 'Show My Products to employees',
    desc: 'Employees see the financial products you have switched on (loans, insurance) and can apply or enroll.',
  },
  {
    key: 'showEmployeeLoans',
    label: 'Show Loans & Advances to employees',
    desc: 'Employees can request loans and salary advances for themselves and track repayments.',
  },
];

export const FinancialProductsSettingsCard: React.FC = () => {
  const [s, setS] = useState<CreditModuleSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    creditApi.getModuleSettings().then(setS).catch(() => setS(null)).finally(() => setLoading(false));
  }, []);

  const toggle = async (key: Key, value: boolean) => {
    if (!s) return;
    setSaving(key);
    setS({ ...s, [key]: value }); // optimistic
    try {
      setS(await creditApi.updateModuleSettings({ [key]: value }));
      resetMarketplaceVisible(); // employees' nav visibility recomputes on next load
    } catch {
      setS({ ...s, [key]: !value }); // revert
    } finally {
      setSaving(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="w-5 h-5 text-orange-600" /> Financial products
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          ROWS.map((r) => (
            <div key={r.key} className="flex items-center justify-between rounded-lg border border-border p-3">
              <div className="min-w-0 pr-3">
                <p className="text-sm font-medium text-foreground">{r.label}</p>
                <p className="text-xs text-muted-foreground">{r.desc}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {saving === r.key ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
                <Switch
                  checked={!!s?.[r.key]}
                  onCheckedChange={(v) => toggle(r.key, v)}
                  disabled={!s || saving === r.key}
                  aria-label={r.label}
                />
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

export default FinancialProductsSettingsCard;
